import { cloneDeep, isBoolean } from 'lodash-es';
import { nextTick, ref, shallowRef } from 'vue';
import { camelize, getFormConfig, isIfShow, message, noop } from '@mts-fe/utils';
import { BasicColumn } from '@mts/components/Card';
import { t } from '@mts/hooks/web/useI18n';
import { usePermission } from '@mts/hooks/web/usePermission';
import { router } from '@/router';
import type { AreaType } from '@/views/lowcode/CFConfig';
import type {
  ActionBtnItem,
  ActionContext,
  BaseFormProps,
  BaseModal,
  BaseModalProps,
  HandleModalOk,
  PageConfig,
  PageModalPayload,
  PageMode,
  ParseOptions,
  RenderTableProps,
  SchemaBasePageProps,
  SearchTableProps,
  TemplateType,
} from '..';
import { createSchemaApi } from '../api';
import { getCFConfigWithCache } from '../api/worker';
import { BaseSchemaConverter, ModalSchemaConverter } from '../utils/converter';
import {
  createSchemaPageContext,
  getActionHookFn,
  getActionRoute,
  getCurrentPageMode,
  getGlobalParams,
  parseActionBtns,
  parseBaseSearch,
  parseBaseTable,
  parseFormSchemas,
  parseRouteMethod,
} from '..';

/** 根据pageCode获取页面配置 */
export const useCFSchema = <T extends PageModalPayload['pageSchema']>(
  pageCode: string,
  callback?: (schema: T) => void,
  options: {
    /** 隐藏loading，默认true */
    silent?: boolean;
  } = {},
) => {
  const schema = ref<T>();
  const template = ref<TemplateType>();

  const { silent = true } = options;
  const hide = !silent ? message.loading(t('common.loadingText'), 0) : noop;
  getCFConfigWithCache(pageCode)
    .then((res) => {
      if (!res) {
        message.error(t('sys.exception.subTitle404'));
        return;
      }

      template.value = res.template;
      schema.value = ModalSchemaConverter[camelize(res.template)](res);
      Object.assign(schema.value!, { getBaseModalProps });
      console.log('>>useCFSchema: ', res, { ...schema.value });

      nextTick(() => callback?.(schema.value!));
    })
    .finally(hide);

  /** 根据区域编码获取弹窗和内部组件的配置 */
  const getBaseModalProps = (
    modalCode: string,
    options?: string | ParseOptions,
  ): BaseModalProps => {
    const cacheKey = [
      pageCode,
      schema.value?.version,
      modalCode,
      JSON.stringify(options || ''),
    ].join('_');
    if (baseModalPropsCache.has(cacheKey)) return cloneDeep(baseModalPropsCache.get(cacheKey)!);

    const props = findSchemaItem(schema.value?.baseModals, modalCode);
    if (!props) return props as any;

    const modalProps = { ...props, _handleModalOk: getHandleModalOk(props) };
    if (!props.insideAreaCode) {
      baseModalPropsCache.set(cacheKey, modalProps);
      return cloneDeep(modalProps);
    }

    const { insideAreaCode } = props;
    const formProps = findSchemaItem(schema.value?.formList, insideAreaCode);
    if (!formProps) {
      console.error(` ${insideAreaCode} is not form area`, schema.value);
      return modalProps;
    }

    const schemas = parseFormSchemas(formProps.schemas, options as ParseOptions);
    const insideProps = getFormConfig<BaseFormProps>({ ...formProps, schemas });

    const result = { ...modalProps, renderMethod: 'showModalForm' as const, insideProps };
    baseModalPropsCache.set(cacheKey, result);
    return cloneDeep(result);
  };
  createSchemaPageContext({ getBaseModalProps });

  return {
    /** @see 建议优先使用callback形式 */
    schema,
    template,
    getPageModal: (modalId: string) => schema.value?.modals?.find((i) => i.modalId === modalId),
  };
};

const formPropsCache = new Map<string, BaseFormProps>();
const searchTablePropsCache = new Map<string, SearchTableProps>();
const baseModalPropsCache = new Map<string, BaseModalProps>();

const OMIT_CARD_PROPS = [
  'gridColumns',
  'actions',
  'tableId',
  'flatParam',
  'showIndexColumn',
  'showBatchBar',
  'showTableSetting',
  'tableSetting',
];

/** 根据pageCode获取页面配置 */
export const useBaseSchema = (
  pageCode: string,
  callback?: (
    /** @readonly 完整配置 */
    schema: SchemaBasePageProps,
  ) => void,
  {
    delay = false,
    silent = true,
    pageMode: curPageMode = getCurrentPageMode(),
  }: {
    /** 延迟加载，开启时默认1秒后加载配置 */
    delay?: boolean | number;
    /** 隐藏loading，默认true */
    silent?: boolean;
    pageMode?: PageMode;
  } = {},
) => {
  const schema = shallowRef<SchemaBasePageProps>();
  const areaTypes = shallowRef<{ areaCode: string; areaType: AreaType }[]>([]);

  const { hasPermission } = usePermission();

  const hide = !silent ? message.loading(t('common.loadingText'), 0) : noop;

  const fetchConfig = () =>
    getCFConfigWithCache(pageCode)
      .then((res) => {
        if (!res) {
          message.error(t('sys.exception.subTitle404'));
          return;
        }

        areaTypes.value = getAreaTypes(res);
        schema.value = BaseSchemaConverter(res);
        console.log('>>useBaseSchema: ', res, { ...schema.value });

        setTimeout(() => callback?.(schema.value!), 0);
      })
      .finally(hide);

  setTimeout(fetchConfig, delay ? (typeof delay === 'number' ? delay : 1000) : 0);

  const getPrefixKey = (key: string) => [pageCode, schema.value?.version, key].join('@');

  /** 根据区域编码获取类表单配置 */
  const getFormPropsByCode = (code: string, options: ParseOptions = {}) => {
    const pageMode = options.pageMode || curPageMode;
    Object.assign(options, { pageMode });
    const cacheKey = getPrefixKey(code + '_' + JSON.stringify({ options }));
    if (formPropsCache.has(cacheKey)) return cloneDeep(formPropsCache.get(cacheKey));

    const props = findSchemaItem(schema.value?.formList, code);
    if (!props) return props;

    const schemas = parseFormSchemas(props.schemas, { pageMode, modals: schema.value?.modalList });
    const result = getFormConfig<BaseFormProps>({ ...props, schemas });
    formPropsCache.set(cacheKey, result);
    return cloneDeep(result);
  };

  /** 根据区域编码获取类查询配置 */
  const getSearchPropsByCode = (code: string) => {
    const props = findSchemaItem(schema.value?.searchList, code);
    if (!props) return props;

    return parseBaseSearch(props, schema.value?.modalList);
  };

  /** 根据区域编码获取类表格配置 */
  const getTablePropsByCode = (code: string, options: ParseOptions = {}) => {
    const props = findSchemaItem(schema.value?.tableList, code);
    if (!props) return props;

    return parseBaseTable(props, {
      pageMode: options.pageMode || curPageMode,
      modals: schema.value?.modalList,
    });
  };

  /** 获取查询表格配置 */
  const getSearchTableProps = (
    table: RenderTableProps | string,
    search?: BaseFormProps | string,
    btns?: ActionBtnItem[] | string,
    options: ParseOptions = {},
  ): SearchTableProps => {
    Object.assign(options, { pageMode: options.pageMode || curPageMode });
    const cacheKey = getPrefixKey(JSON.stringify({ table, search, btns, options }));

    if (searchTablePropsCache.has(cacheKey)) {
      const result = searchTablePropsCache.get(cacheKey)!;
      return cloneDeep(result);
    }

    const searchProps = typeof search === 'string' ? getSearchPropsByCode(search) : search;
    const tableProps = typeof table === 'string' ? getTablePropsByCode(table, options) : table;
    const btnsProps = typeof btns === 'string' ? getBtnsPropsByCode(btns, options) : btns;

    const result: SearchTableProps = {
      useSearchForm: !!searchProps,
      formConfig: searchProps,
      ...tableProps,
      columns: tableProps?.columns || [],
      headerBtns: btnsProps,
      modals: schema.value?.modalList,
    };

    searchTablePropsCache.set(cacheKey, result);
    return cloneDeep(result);
  };

  /** 根据区域编码获取单个卡片配置 */
  const getCardItemProps = (code: string, options: ParseOptions = {}) => {
    const props = findSchemaItem(schema.value?.tableList, code);
    if (!props) return props;

    const globalParams = getGlobalParams();
    const { columns, ...cardProps } = parseBaseTable(props, {
      pageMode: options.pageMode || curPageMode,
      modals: schema.value?.modalList,
    });

    const omitSet = new Set(OMIT_CARD_PROPS);
    const result: Recordable = {};
    for (const key in cardProps) {
      if (!omitSet.has(key)) {
        result[key] = cardProps[key];
      }
    }

    result.columns = columns
      .filter((col) => hasPermission(col.auth) && isIfShow<BasicColumn>(col, globalParams))
      .map((col) => {
        const title = col.title;
        const dataLangKey = typeof title === 'string' && t(title) !== title ? title : '';
        return { ...col, dataLangKey, title: t(title as string) };
      });
    return result;
  };

  /** 根据区域编码获取类按钮配置 */
  const getBtnsPropsByCode = (code: string, options: ParseOptions = {}) => {
    const props = findSchemaItem(schema.value?.btnList, code);
    if (!props) return [];
    return parseActionBtns(props.buttons || [], options);
  };

  const byCodeFnMap = {
    BASE_FORM: getFormPropsByCode,
    BASE_SEARCH: getSearchPropsByCode,
    BASE_TABLE: getSearchTableProps,
    BASE_CARD: getSearchTableProps,
    BASE_BTNS: getBtnsPropsByCode,
  };

  /** 根据区域编码获取弹窗和内部组件的配置 */
  const getBaseModalProps = (
    modalCode: string,
    searchCode?: string | ParseOptions,
    btnCode?: string,
  ): BaseModalProps => {
    const cacheKey = getPrefixKey(
      [
        modalCode,
        typeof searchCode === 'string' ? searchCode : JSON.stringify(searchCode),
        btnCode,
      ].join('_'),
    );
    if (baseModalPropsCache.has(cacheKey)) return cloneDeep(baseModalPropsCache.get(cacheKey)!);

    const props = findSchemaItem(schema.value?.baseModals, modalCode);
    if (!props) return props as any;

    const modalProps = { ...props, _handleModalOk: getHandleModalOk(props) };
    if (!props.insideAreaCode) {
      baseModalPropsCache.set(cacheKey, modalProps);
      return cloneDeep(modalProps);
    }

    const { insideAreaCode, addonSearchCode, addonBtnCode, addonFormCode } = props;
    const areaInfo = areaTypes.value.find((i) => i.areaCode === insideAreaCode);

    if (!areaInfo) {
      console.error(` ${insideAreaCode} not found areaType`, areaTypes.value);
      return modalProps;
    }

    const insideProps = byCodeFnMap[areaInfo.areaType]?.(
      insideAreaCode,
      searchCode || addonSearchCode,
      btnCode || addonBtnCode,
    );
    const result = {
      ...modalProps,
      renderMethod: ('showModal' +
        (areaInfo.areaType[5] +
          areaInfo.areaType.slice(6).toLowerCase())) as BaseModalProps['renderMethod'],
      formProps: addonFormCode ? getFormPropsByCode(addonFormCode) : void 0,
      insideProps,
    };
    baseModalPropsCache.set(cacheKey, result);
    return cloneDeep(result);
  };

  /** 根据区域编码获取弹窗配置 */
  const getModalPropsByCode = (code: string) => findSchemaItem(schema.value?.modalList, code);

  const whenIdleRun = (callback: () => void) => requestIdleCallback(callback);

  if (!delay) createSchemaPageContext({ pageSchema: schema, getBaseModalProps });

  return {
    /** @readonly @deprecated 不建议直接使用 */
    schema,
    /** 空闲时执行（提升非首屏加载时的性能） */
    whenIdleRun,
    /** @public 根据区域编码获取类表单配置 */
    getFormPropsByCode,
    /** @deprecated 根据区域编码获取类查询配置 */
    getSearchPropsByCode,
    /** @public 获取查询表格配置 */
    getSearchTableProps,
    /** @public 根据区域编码获取单个卡片配置 */
    getCardItemProps,
    /** @public 根据区域编码获取类按钮配置 */
    getBtnsPropsByCode,
    /** @public 根据区域编码获取弹窗和内部组件的配置 */
    getBaseModalProps,
    /** @deprecated 根据区域编码获取弹窗配置 */
    getModalPropsByCode,
  };
};

export type BaseSchemaReturn = ReturnType<typeof useBaseSchema>;

const findSchemaItem = <T>(list: T[] | undefined, code: string): T | undefined => {
  if (!list || !code) return undefined;
  return list.find((item: any) => item._ID === code);
};

const getHandleModalOk = (props: BaseModal): HandleModalOk => {
  const { eventApi, routeUrl, silent = true, modalHandler = {} } = props;
  const { beforeApiFn, afterApiFn } = modalHandler;
  return async (record = {}, modalAction) => {
    const context: ActionContext = { record, modalAction, message, router, t };
    modalAction.changeOkLoading?.(true);
    try {
      let data = record;

      if (beforeApiFn) {
        const res = await getActionHookFn(beforeApiFn)(record, context, void 0);
        if (res !== null && typeof res === 'object') {
          data = res;
        }
        if (typeof res === 'boolean' && !res) {
          modalAction.changeOkLoading?.(false);
          return false;
        }
      }

      if (eventApi) {
        const res = await createSchemaApi(eventApi)?.(data);
        if (res && !isBoolean(res)) {
          data = res;
        }
        !silent && message.success(t('common.opSuccess'));
      }
      modalAction.setModalProps?.({ confirmLoading: false, open: false });

      if (afterApiFn) {
        await getActionHookFn(afterApiFn)(data, context, void 0);
      }

      if (routeUrl) {
        nextTick(() => {
          const [path, method] = parseRouteMethod(getActionRoute(routeUrl, data));
          method === 'go' ? router[method](+path) : router[method](path);
        });
        return false;
      }
      return data;
    } catch (error) {
      modalAction.changeOkLoading?.(false);
      console.error(error);
      throw new Error(error);
    }
  };
};

const getAreaTypes = (pageConfig: PageConfig) =>
  pageConfig.areaDTOList.map((i) => ({
    areaCode: i.areaCode!,
    areaType: i.areaType,
  }));
